// Axios client cho cnvwork-saas API. Tự động:
//   - Authorization: Bearer <jwt> (từ authStore)
//   - Idempotency-Key cho mọi mutation (ADR-008)
//   - Refresh token khi 401 (singleton promise — concurrent 401 reuse cùng 1 refresh)
//   - 429 Retry-After backoff (max 1 retry)
//   - Auth-broken circuit (Chunk B 2026-05-28): sau khi refresh fail → set
//     _authBroken=true, mọi request mới REJECT trước khi gửi network. Tránh
//     loop polling/realtime spam BE sau khi token đã chết.
//   - Toast error cho domain errors
//   - Accept-Language = ngôn ngữ đang hiển thị (câu lỗi BE ở cửa chưa đăng nhập)

import axios from 'axios';
import toast from 'react-hot-toast';
import { getLocale, i18n } from '@cnv/ui/i18n';
import { msg } from '@lingui/core/macro';
import { useAuthStore } from './authStore';
import { resolveApiBase } from './apiBase';
import { deviceTimezone } from './deviceTimezone';

function newIdempotencyKey() {
  return crypto.randomUUID();
}

// Export: hub.cnvwork.com KHÔNG proxy /api (nginx chỉ serve SPA + /ws) nên mọi fetch() dùng
// đường dẫn TƯƠNG ĐỐI sẽ rơi vào static server của hub và không bao giờ tới BE. Ai gọi fetch
// trực tiếp (beacon, keepalive flush) PHẢI ghép baseURL này.
export const baseURL = resolveApiBase();

export const inboxClient = axios.create({
  baseURL,
  timeout: 30_000,
});

// ─── Reference-data cache (perf) ────────────────────────────────────────────
// /users/simple gọi ở ~50 file, /teams/scoped ~11, + vài config CRM — đều là data
// ÍT ĐỔI nhưng fetch lại mỗi page/modal. Cache transparent (TTL 60s) + in-flight dedup:
// nhiều component mount cùng lúc → CHỈ 1 request. Whitelist path chính xác (an toàn — request
// khác KHÔNG cache). mutate xong gọi clearReferenceCache() để badge/list cập nhật ngay.
const REF_CACHE_TTL_MS = 60_000;
const REF_CACHE_PATHS = new Set([
  '/users/simple', '/teams/scoped', '/crm/sources', '/crm/pipelines',
  '/crm/industries', '/crm/deal-roles', '/crm/product-categories',
]);
const _refCache = new Map();    // key → { response, expiry }
const _refInflight = new Map(); // key → Promise
export function clearReferenceCache() { _refCache.clear(); _refInflight.clear(); }
const _origGet = inboxClient.get.bind(inboxClient);
inboxClient.get = function cachedGet(url, config) {
  if (!REF_CACHE_PATHS.has(url)) return _origGet(url, config);
  const key = url + '?' + JSON.stringify(config?.params || {});
  const now = Date.now();
  const hit = _refCache.get(key);
  if (hit && hit.expiry > now) return Promise.resolve(hit.response);
  if (_refInflight.has(key)) return _refInflight.get(key);          // dedup burst on-load
  const p = _origGet(url, config)
    .then((r) => { _refCache.set(key, { response: r, expiry: now + REF_CACHE_TTL_MS }); _refInflight.delete(key); return r; })
    .catch((e) => { _refInflight.delete(key); throw e; });
  _refInflight.set(key, p);
  return p;
};

// ─── Singleton refresh state (Chunk B) ──────────────────────────
// `_refreshing` — Promise<accessToken> | null. Concurrent 401 đợi cùng promise.
// `_authBroken` — sau khi refresh fail HOẶC có lệnh logout. Mọi request mới reject
//   trực tiếp, KHÔNG gửi network. Reset = false khi user login lại thành công.
// `_refreshListeners` — chatStomp / push subscribe sau refresh để recompute headers.
let _refreshing = null;
let _authBroken = false;
const _refreshListeners = new Set();
const _authBrokenListeners = new Set();

export function isAuthBroken() { return _authBroken; }

/** Subscribe — fn(newAccessToken) khi refresh thành công. Return unsubscribe. */
export function onTokenRefreshed(fn) {
  _refreshListeners.add(fn);
  return () => _refreshListeners.delete(fn);
}

/** Subscribe — fn() khi _authBroken transition false → true. Return unsubscribe. */
export function onAuthBroken(fn) {
  _authBrokenListeners.add(fn);
  return () => _authBrokenListeners.delete(fn);
}

// ─── Phân loại lỗi refresh (2026-07-25) ──────────────────────────────────────
// GỐC BUG "SaaS logout hoài": trước đây `catch (e) { _markAuthBroken() }` bắt MỌI lỗi — mất
// mạng, laptop ngủ dậy, 502 khi deploy (đo prod: 81 cụm 502/15 ngày do `pm2 restart` không
// drain), preflight CORS fail (hub.cnvwork.com gọi api.cnvwork.com cross-origin nên MỌI refresh
// đều có OPTIONS đi trước) — rồi XOÁ luôn refresh token. Một lần trượt mạng = mất phiên vĩnh
// viễn, F5 cũng không cứu. Đo prod 14 ngày: 258 lần đăng nhập lại nhưng chỉ 88 lượt 401 trên
// /auth/refresh ⇒ ~78% lần bị đá không hề có BE từ chối.
//
// Luật mới: CHỈ huỷ phiên khi server THẬT SỰ từ chối token (401/403). Mọi thứ khác là lỗi tạm
// → retry backoff, GIỮ NGUYÊN localStorage. Mobile đã làm đúng như vậy từ 2026-05-20
// (cnvwork-mobile/lib/core/api/auth_interceptor.dart:150) — đây là port lại cho web.
export class RefreshTransientError extends Error {
  constructor(reason, cause) {
    super('refresh_transient');
    this.name = 'RefreshTransientError';
    this.reason = reason;             // network_error | timeout | http_5xx | rate_limited
    this.cause = cause;
  }
}

/** Phân loại 1 lỗi axios thành reason chuẩn (khớp enum BE POST /auth/session-lost). */
function _classifyRefreshError(e) {
  const status = e?.response?.status;
  if (status === 401) return 'http_401';
  if (status === 403) return 'http_403';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'http_5xx';
  if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) return 'timeout';
  // Không có response = request không tới nơi: mất mạng, DNS, TLS reset, HOẶC preflight CORS fail.
  // Trình duyệt cố tình không phân biệt được preflight-fail với network-fail (bảo mật) → gộp.
  if (!e?.response) return 'network_error';
  return 'unknown';
}

/** Chỉ 401/403 mới là "server từ chối token" — phần còn lại là hạ tầng. */
function _isTokenRejected(reason) {
  return reason === 'http_401' || reason === 'http_403';
}

// Client RIÊNG cho /auth/refresh: có timeout (axios mặc định = 0 = chờ vô hạn) và KHÔNG dính
// interceptor của inboxClient (tránh đệ quy refresh-trong-refresh).
const authClient = axios.create({ baseURL, timeout: 15_000 });

const REFRESH_RETRY_DELAYS_MS = [1000, 3000, 6000];

const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Singleton refresh — concurrent 401 reuse cùng 1 promise.
 *
 * Throw `RefreshTransientError` khi lỗi hạ tầng (phiên VẪN CÒN, caller nên coi như lỗi mạng),
 * throw lỗi gốc khi server từ chối token (phiên đã chết → đã _markAuthBroken).
 * Export để chatStomp + ws clients dùng chung lock thay vì self-implement.
 */
export async function refreshAccessToken() {
  if (_authBroken) throw new Error('auth_broken');
  if (_refreshing) return _refreshing;

  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) {
    _markAuthBroken('no_refresh_token');
    throw new Error('no_refresh_token');
  }

  _refreshing = (async () => {
    let lastReason = 'unknown';
    let lastErr = null;
    try {
      for (let attempt = 0; attempt <= REFRESH_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
          // supports_rotation: khai với BE rằng client này CHỊU lưu refresh token mới (xử lý ngay
          // dưới đây). Không khai thì BE không rotate — xem api schemas.refreshInput.
          const resp = await authClient.post('/auth/refresh', { refreshToken, supports_rotation: true });
          const newToken = resp.data.accessToken;
          useAuthStore.getState().setSession({
            accessToken: newToken,
            // BE rotate refresh token (sliding window) → PHẢI ghi đè bản đang lưu, nếu không
            // lần refresh sau vẫn gửi token cũ và rơi ra ngoài grace window. Response cũ không
            // có field này → authStore giữ token hiện tại (tương thích 2 chiều).
            refreshToken: resp.data.refreshToken,
            user: resp.data.user,
            active_tenant: resp.data.active_tenant,
          });
          for (const fn of _refreshListeners) { try { fn(newToken); } catch {} }
          return newToken;
        } catch (e) {
          lastErr = e;
          lastReason = _classifyRefreshError(e);
          // Server từ chối token → phiên chết thật, không retry.
          if (_isTokenRejected(lastReason)) {
            _markAuthBroken(lastReason, { http_status: e?.response?.status ?? null });
            throw e;
          }
          const delay = REFRESH_RETRY_DELAYS_MS[attempt];
          if (delay == null) break;                 // hết lượt retry
          await _sleep(delay);
        }
      }
      // Hết retry mà vẫn lỗi hạ tầng: GIỮ PHIÊN. Đây là điểm khác biệt cốt lõi so với code cũ.
      throw new RefreshTransientError(lastReason, lastErr);
    } finally {
      _refreshing = null;
    }
  })();

  return _refreshing;
}

/**
 * Huỷ phiên + về /login. CHỈ gọi khi phiên thật sự không dùng được nữa.
 * @param {string} reason  khớp enum BE (http_401|http_403|no_refresh_token|account_disabled|...)
 */
function _markAuthBroken(reason = 'unknown', extra = {}) {
  if (_authBroken) return;
  _authBroken = true;
  _reportSessionLost(reason, extra);
  // FORCE clear localStorage — tránh stale refreshToken phục hồi sau F5.
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('cnvwork-saas-auth');
    }
  } catch {}
  useAuthStore.getState().logout();
  for (const fn of _authBrokenListeners) { try { fn(); } catch {} }
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    toast.error(i18n._(msg`Phiên đăng nhập hết hạn — đăng nhập lại`), { duration: 3000 });
    // .replace() — không add history entry để user back button không quay lại trang lỗi
    window.location.replace('/login');
  }
}

// Beacon "phiên bị mất" → BE đếm theo reason (POST /auth/session-lost, 204, không auth).
// KHÔNG await, KHÔNG để lỗi ở đây chặn logout.
//
// TẠI SAO fetch(keepalive) MÀ KHÔNG navigator.sendBeacon: prod build có
// VITE_API_BASE_URL=https://api.cnvwork.com/api/v1 (admin/.env.production) nên đây là request
// CROSS-ORIGIN từ hub.cnvwork.com. sendBeacon chỉ gửi được content-type trong danh sách an toàn
// CORS — 'application/json' sẽ cần preflight mà sendBeacon không làm được ⇒ bị chặn im lặng.
// fetch với keepalive:true làm được preflight VÀ sống sót qua điều hướng
// (window.location.replace chạy ngay mấy dòng dưới).
function _reportSessionLost(reason, extra = {}) {
  // Tách try: parse tuổi token hỏng thì vẫn PHẢI gửi được beacon (mất beacon = mất đúng thứ
  // mình dựng endpoint này để đo).
  let ageSec = null;
  try {
    const rt = useAuthStore.getState().refreshToken;
    if (rt) {
      const payload = JSON.parse(atob(rt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload?.iat) ageSec = Math.max(0, Math.round(Date.now() / 1000 - payload.iat));
    }
  } catch { /* không đọc được tuổi token — vẫn gửi beacon với age null */ }
  try {
    const body = JSON.stringify({
      reason,
      client_type: _clientType(),
      refresh_token_age_sec: ageSec,
      http_status: extra.http_status ?? null,
      path: typeof window !== 'undefined' ? window.location.pathname.slice(0, 200) : null,
    });
    fetch(`${baseURL}/auth/session-lost`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {});
  } catch { /* telemetry KHÔNG bao giờ được làm hỏng luồng logout */ }
}

function _clientType() {
  try {
    if (/Electron/i.test(navigator.userAgent)) return 'desktop';
    return 'web';
  } catch { return 'web'; }
}

/** Reset _authBroken sau khi login lại — gọi từ AuthContext sau setSession thành công. */
export function resetAuthBroken() {
  _authBroken = false;
}

// ─── Endpoints được phép gọi khi _authBroken=true (public + login) ────
const AUTH_BROKEN_ALLOWLIST = [
  // '/auth/logout': người dùng đang RỜI ĐI — tuyệt đối không cho nó kích proactive refresh
  // (ladder retry tới ~70s/lượt × 3 lần retry transient = bấm Đăng xuất mà app đứng vài phút).
  '/auth/login', '/auth/google', '/auth/refresh', '/auth/logout',
  '/auth/apple/', '/auth/zalo', '/auth/identity/',
  // Quên mật khẩu: người dùng vào /forgot-password CHÍNH VÌ vừa bị đá phiên, tức
  // `_authBroken` đang là true. Thiếu prefix này thì request bị reject NGAY TRƯỚC khi ra
  // network — trang báo lỗi mà tab Network trống trơn, gần như không thể lần ra nguyên nhân.
  // ⚠️ Phải là `/auth/password/` (có gạch cuối): `/auth/change-password` là endpoint CẦN
  // đăng nhập, không được lọt vào allowlist này.
  '/auth/password/',
  '/onboarding/', '/public/',
];

function _isAllowedWhenBroken(url = '') {
  return AUTH_BROKEN_ALLOWLIST.some(p => url.startsWith(p) || url.includes(p));
}

// Decode `exp` (ms) từ JWT access token. null nếu không parse được.
function _tokenExpMs(token) {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    return payload?.exp ? payload.exp * 1000 : null;
  } catch { return null; }
}

// Token đã/sắp hết hạn trong < 30s → cần refresh TRƯỚC khi gửi request.
// Không decode được exp → false (để 401-retry phía response lo, tránh refresh thừa).
function _isTokenExpiringSoon(token) {
  const expMs = _tokenExpMs(token);
  if (!expMs) return false;
  return Date.now() >= expMs - 30_000;
}

/**
 * ensureFreshToken — trả access token còn hạn, refresh CHỦ ĐỘNG nếu sắp hết hạn.
 * Dùng chung cho mọi axios client (inboxClient, chatApi._client) + socket → 1 nguồn
 * token authority duy nhất, KHÔNG mỗi client tự xử lý refresh lệch nhau.
 * @returns {Promise<string|null>} token mới/đang dùng, null nếu auth broken / refresh fail.
 */
export async function ensureFreshToken() {
  if (_authBroken) return null;
  const current = useAuthStore.getState().accessToken;
  let token = current;
  if (token && _isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken();
    } catch (e) {
      // Lỗi hạ tầng → phiên VẪN CÒN. Trả token hiện tại (có thể đã hết hạn) thay vì null: caller
      // gửi đi và cùng lắm nhận 401 từ BE rồi tự retry, KHÔNG ai bị đăng xuất. Trả null ở đây
      // từng làm chat/socket rơi vào nhánh "không có token" mỗi lần mạng chớp.
      if (e instanceof RefreshTransientError) return current || null;
      return null;
    }
  }
  return token || null;
}

inboxClient.interceptors.request.use(async (config) => {
  // Chunk B: short-circuit khi auth đã broken — không gửi network.
  // Tiết kiệm bandwidth + tránh loop polling spam BE sau logout.
  if (_authBroken && !_isAllowedWhenBroken(config.url || '')) {
    const err = new Error('auth_broken');
    err.code = 'AUTH_BROKEN';
    err.config = config;
    err.response = { status: 401, data: { error: { code: 'auth_broken' } } };
    return Promise.reject(err);
  }

  let token = useAuthStore.getState().accessToken;

  // PROACTIVE REFRESH (2026-05-28): access token TTL 15p → trước đây mỗi 15p các poll
  // (sidebar badges, voice/online-count, chat/config...) bắn với token HẾT HẠN → burst
  // 401 → refresh → retry, lặp "hoài" gây spam console + tải BE. Giờ: nếu token sắp hết
  // hạn → refresh TRƯỚC (singleton, concurrent reuse 1 promise) → request đi với token mới
  // → KHÔNG còn 401. Skip auth endpoints (login/refresh tự lo).
  if (token && !_isAllowedWhenBroken(config.url || '') && _isTokenExpiringSoon(token)) {
    try {
      token = await refreshAccessToken();
    } catch (err) {
      if (err instanceof RefreshTransientError) {
        // Lỗi HẠ TẦNG, phiên vẫn còn. Reject dưới dạng lỗi mạng (KHÔNG có `response`) để rơi
        // vào lưới auto-retry transient của response interceptor bên dưới + hiện overlay
        // "Kết nối Internet không ổn định". TUYỆT ĐỐI không giả 401 ở đây — đó chính là đường
        // biến một lần rớt wifi thành đăng xuất.
        const e = new Error('refresh_transient');
        e.code = 'REFRESH_TRANSIENT';
        e.config = config;
        return Promise.reject(e);
      }
      // Token bị server từ chối → refreshAccessToken đã _markAuthBroken + redirect /login.
      const e = new Error('auth_broken');
      e.code = 'AUTH_BROKEN';
      e.config = config;
      e.response = { status: 401, data: { error: { code: 'auth_broken' } } };
      return Promise.reject(e);
    }
  }

  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Ngôn ngữ ĐANG HIỂN THỊ, đọc lúc GỬI — đổi ngôn ngữ giữa chừng thì request sau đổi theo.
  // Cửa chưa đăng nhập (đăng nhập, quên/đặt lại mật khẩu, nhận lời mời) không có JWT nên máy chủ
  // chọn ngôn ngữ câu lỗi theo header này (api/src/shared/i18nBe.js `localeOf`). Không gắn thì
  // trình duyệt tự gửi ngôn ngữ của MÁY: người chọn tiếng Việt trên Chrome tiếng Anh nhận câu
  // tiếng Anh. Có JWT thì claim trong token thắng, header vô hại. Chỗ gọi tự đặt header thì giữ.
  if (!config.headers.has('Accept-Language')) {
    config.headers.set('Accept-Language', getLocale());
  }

  const isMutation = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase());
  if (isMutation && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = newIdempotencyKey();
  }
  return config;
});

/** Số giây chờ → chuỗi người đọc được. Bucket ngày có thể trả tới 86400s: "1440 phút" là
 *  con số vô nghĩa với user, phải quy ra giờ. */
function formatWait(sec) {
  if (sec >= 7200) { const gio = Math.ceil(sec / 3600); return i18n._(msg`${gio} giờ`); }
  if (sec >= 120) { const phut = Math.ceil(sec / 60); return i18n._(msg`${phut} phút`); }
  return i18n._(msg`${sec} giây`);
}

inboxClient.interceptors.response.use(
  (res) => {
    // Request thành công sau khi đã retry transient → ẩn overlay "Kết nối Internet không ổn định".
    if (res.config?._retryCount > 0) {
      try { window.dispatchEvent(new CustomEvent('server-retry-resolved')); } catch {}
    }
    return res;
  },
  async (err) => {
    const original = err.config;
    if (!original) return Promise.reject(err);

    // ───── Auto-retry lỗi tạm thời (mất mạng / server bận) + overlay ─────
    // !response = network/timeout; 502/503/504 = server bận. Retry tối đa 3 lần × 3s.
    // Bắn 'server-retry' → ServerRetryOverlay hiện "Kết nối Internet không ổn định".
    // Skip upload (retry = upload lại bytes) — fail nhanh để toast.
    const _st = err.response?.status;
    // 502/503/504 chỉ được coi là "server bận" khi KHÔNG có lỗi nghiệp vụ có cấu trúc.
    // BE trả {error:{code}} nghĩa là request đã tới nơi và bị từ chối có lý do → retry
    // 3 lần là gửi lại thao tác 3 lần (vd yêu cầu xin quyền gọi Zalo) và bung overlay
    // "Kết nối Internet không ổn định" dù mạng vẫn tốt.
    const _hasDomainError = !!err.response?.data?.error?.code;
    const _isTransient = (!err.response || _st === 502 || _st === 503 || _st === 504) && !_hasDomainError;
    const _isUpload = /\/(storage\/upload|files\/upload)/.test(original.url || '');
    // Đường AI tuyển dụng: mỗi lượt gọi hãng Claude tốn TIỀN THẬT và mất 30-60s. Timeout ở
    // trình duyệt KHÔNG huỷ được lượt đã bay sang hãng ⇒ thử lại = trả tiền lần nữa cho đúng
    // việc máy chủ đang làm dở. Đo Core 16/09/2026: một cú bấm «AI lập tiêu chí» thành BA
    // lượt gọi hãng cách nhau ~32s. Các đường này tự đặt timeout 180s (cnv-ui
    // lib/recruitmentAi.js AI_CALL_TIMEOUT_MS) nên không cần lưới retry nữa.
    // Hộp thư «AI rep giúp» / «Gợi ý AI» cũng là lượt gọi hãng (đo Core 16/09/2026: 18,5s cho
    // một bản nháp ở agent có kho tri thức) ⇒ cùng luật: hạn chờ 180s, không retry.
    const _isAiCall = /\/recruitment\/(ai\/|campaigns\/[^/]+\/ai-criteria|candidates\/[^/]+\/ai-(screen|attach))|\/ai-(draft|suggest)-reply/.test(original.url || '');
    if (_isTransient && !_isUpload && !_isAiCall) {
      if (original._retryCount == null) original._retryCount = 0;
      if (original._retryCount < 3) {
        original._retryCount++;
        try { window.dispatchEvent(new CustomEvent('server-retry', { detail: { attempt: original._retryCount } })); } catch {}
        await new Promise(r => setTimeout(r, 3000));
        return inboxClient(original);
      }
      try { window.dispatchEvent(new CustomEvent('server-retry-resolved')); } catch {}
    }

    // ───── Chunk B: 429 Retry-After backoff (max 1 retry) ─────
    // Chunk A BE trả Retry-After header → FE đợi đúng số giây rồi retry 1 lần.
    // Cap 30s tránh stuck UI quá lâu. Không retry mutation idempotent-conflict.
    if (err.response?.status === 429 && !original._retried429) {
      original._retried429 = true;
      const retryAfter = parseInt(err.response.headers?.['retry-after'], 10);
      if (Number.isFinite(retryAfter) && retryAfter > 0 && retryAfter <= 30) {
        await new Promise(r => setTimeout(r, retryAfter * 1000));
        return inboxClient(original);
      }
      // Header missing hoặc bất hợp lý → toast + reject.
      // `id` cố định = react-hot-toast THAY THẾ toast cũ thay vì xếp chồng. Bắt buộc: 1 lần
      // mở app bắn hàng chục request song song, dính rate limit là dính CẢ LOẠT → không dedupe
      // thì user thấy tường 15-20 toast đỏ giống hệt nhau che kín màn hình (sự cố 2026-07-29).
      const waitSec = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null;
      toast.error(
        waitSec && waitSec > 30
          ? i18n._(msg`Hệ thống đang giới hạn truy cập — thử lại sau ${formatWait(waitSec)}`)
          : i18n._(msg`Thao tác quá nhanh — thử lại sau vài giây`),
        { duration: 4000, id: 'rate_limited' },
      );
      return Promise.reject(err);
    }

    // ───── 401 → singleton refresh + retry (Chunk B refactor) ─────
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      const reqUrl = original.url || '';
      // /auth/refresh tự fail 401 → refresh token chết thật → auth broken. KHÔNG retry chính nó.
      if (reqUrl.endsWith('/auth/refresh')) {
        _markAuthBroken('http_401', { http_status: 401 });
        return Promise.reject(err);
      }
      // Auth-flow endpoints khác (login/google/zalo/apple/identity/onboarding/public) trả 401
      // = SAI credential / OTP hết hạn, KHÔNG phải session expired. TUYỆT ĐỐI không kích refresh
      // circuit — nếu không 1 lần nhập sai mật khẩu sẽ gọi refreshAccessToken() → không có
      // refreshToken → _markAuthBroken (toast "hết phiên" + redirect /login + throw 'auth_broken'
      // unhandled cho các request batched đang chạy). Để LoginPage/SignupPage tự map lỗi
      // credential. (GlitchTip issue 8 — Error: auth_broken onunhandledrejection)
      if (_isAllowedWhenBroken(reqUrl)) {
        return Promise.reject(err);
      }
      try {
        const newToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newToken}`;
        return inboxClient(original);
      } catch (e) {
        if (e instanceof RefreshTransientError) {
          // Không refresh được vì hạ tầng — GIỮ PHIÊN. Rejection này phát sinh BÊN TRONG response
          // interceptor nên không quay lại được lưới transient ở trên; bắn overlay để user thấy
          // "Kết nối Internet không ổn định" thay vì thao tác im lặng không phản hồi.
          try { window.dispatchEvent(new CustomEvent('server-retry', { detail: { attempt: 1 } })); } catch {}
          const t = new Error('refresh_transient');
          t.code = 'REFRESH_TRANSIENT';
          t.config = original;
          return Promise.reject(t);
        }
        // _markAuthBroken đã được gọi trong refreshAccessToken nếu token bị từ chối
        return Promise.reject(err);
      }
    }

    // Toast domain error — map sang tiếng Việt friendly thay vì show raw code (TC-183).
    // SKIP auto-toast cho các endpoint /auth/* (login, google, refresh, onboarding) —
    // LoginPage + SignupPage tự handle error với mapping cụ thể của riêng nó. Nếu interceptor
    // toast trước thì user sẽ thấy 2 toast (raw + friendly) như case TC-219.
    const url = original?.url || '';
    const isAuthFlow = /\/(auth|onboarding)\//.test(url) || url.endsWith('/auth/login') || url.endsWith('/auth/google') || url.endsWith('/auth/refresh');
    const code = err.response?.data?.error?.code;
    const message = err.response?.data?.error?.message;
    const details = err.response?.data?.error?.details;
    // module_disabled: điều kiện BÌNH THƯỜNG khi tenant không mua module (vd team_chat).
    // Forbidden(scope,msg) → code='scope_required' + details.scope='module_disabled'
    // (api/src/shared/errors.js). PHẢI check details.scope, KHÔNG phải code. Caller global
    // (TeamChatPushNotifications, unread badge...) đã .catch — KHÔNG toast message kỹ thuật
    // "Module team_chat chưa enable..." cho end user. (TC-321/326/328/362)
    const SKIP_TOAST_CODES = ['idempotency_conflict', 'auth_broken'];
    const isModuleDisabled = details?.scope === 'module_disabled';
    // ─── Chống 2 toast CÙNG MỘT CÂU cho lỗi của thao tác ghi ───
    // Component cnv-ui nào cũng tự `toast.error(err.response.data.message || 'Lỗi ...')` trong
    // catch, mà `data.message` (BE đẩy ra khi lỗi có `userMessage`, đã dịch theo ngôn ngữ người
    // dùng — api/src/middleware/errorHandler.js) cũng chính là câu interceptor bắn. Toast thêm =
    // user thấy ĐÚNG một câu HAI lần: ảnh khách gửi 31/07; ở Work 14/09 lan sang mọi 403 mà đợt
    // vá IDOR gắn câu (Hộp thư, chuyển giao dữ liệu CRM, chia sẻ đơn...).
    //
    // Nhường cho component, NHƯNG KHÔNG nhường mù: cnv-ui còn ~300 `catch {}` (đo bằng
    // tools/scan-fe-dead-handlers.js) nên nhường thẳng là có đường thành im lặng hoàn toàn —
    // đúng thứ feedback_no_silent_fail_fe cấm. Cách làm: hoãn 250ms rồi soi vùng toast xem đã có
    // toast chứa ĐÚNG câu mình sắp bắn chưa (`isToastShowing`). Có rồi thì thôi, chưa có thì tự bắn.
    // - So bằng CHÍNH CÂU, không bằng cụm chữ ký: bản trước dò "không phải lỗi hệ thống" ⇒ người
    //   dùng en/fr không bao giờ khớp nên vẫn 2 toast, và chỉ phủ mỗi `permission_denied`.
    // - Không "có toast nào đó": toast success cũ còn nằm đó sẽ làm nhận nhầm.
    // - Áp cho MỌI code của mutation: câu so là `text` bên dưới, nên lỗi chưa có `data.message` mà
    //   component đọc `error.message` (vd `scope_required` kèm câu — formatErrorForUser trả nguyên
    //   câu đó) cũng hết đúp.
    // GET giữ nguyên đường cũ (caller load list/badge phần lớn im lặng, mất toast là 403 lúc mở
    // trang không còn dấu vết).
    const isMutationReq = ['post', 'put', 'patch', 'delete'].includes(String(original?.method || '').toLowerCase());
    const beUserMessage = typeof err.response?.data?.message === 'string' ? err.response.data.message : null;
    // Opt-out per-request: caller nào tự map lỗi domain sang câu tiếng Việt riêng
    // thì truyền { skipErrorToast: true } — nếu không user thấy 2 toast (raw code
    // của interceptor + câu friendly của caller). Cùng lý do với isAuthFlow ở trên,
    // nhưng không phải hardcode thêm URL vào interceptor mỗi lần có case mới.
    if (code && !SKIP_TOAST_CODES.includes(code) && !isModuleDisabled && !isAuthFlow
        && !original?.skipErrorToast) {
      // Ưu tiên câu BE viết cho NGƯỜI (`data.message` — errorHandler chỉ đẩy ra khi error có
      // `userMessage`). BE biết ngữ cảnh nên câu luôn sát hơn; `formatErrorForUser` là đường
      // dự phòng cho error chưa khai `userMessage` và cho BE bản cũ chưa deploy.
      const text = beUserMessage || formatErrorForUser({ code, message, details });
      // Riêng lỗi quyền: gắn `id` để dedupe. Mở 1 trang là bắn hàng loạt request, thiếu quyền thì
      // thiếu CẢ LOẠT → không dedupe là phủ kín màn hình bằng cùng một câu (cùng lý do với
      // 'rate_limited' ở trên). Code khác giữ nguyên hành vi cũ, không đụng.
      const dedupeId = (code === 'permission_denied' || code === 'scope_required') ? `domain_${code}` : undefined;
      const opts = dedupeId ? { id: dedupeId } : undefined;
      if (isMutationReq) {
        // Xem "Chống 2 toast" bên trên. `catch {}` bọc ngoài vì đây là nhánh chỉ để KHÔNG mất
        // thông báo — lỗi trong lúc dò DOM tuyệt đối không được làm gãy luồng lỗi gốc.
        setTimeout(() => {
          try {
            if (!isToastShowing(text)) toast.error(text, opts);
          } catch { toast.error(text, opts); }
        }, 250);
      } else {
        toast.error(text, opts);
      }
    }
    return Promise.reject(err);
  },
);

// Trên màn hình đã có toast chứa đúng câu `text` chưa — cho nhánh hoãn "Chống 2 toast" ở trên.
// Chỉ soi toast của react-hot-toast (`[data-rht-toaster]`, ô chữ mang role="status"): role="status"
// còn gặp ở chỗ khác ngoài toast. `textContent` + gộp khoảng trắng vì câu có thể xuống dòng;
// `includes` vì component có thể ghép câu vào một câu dài hơn.
function isToastShowing(text) {
  const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const want = norm(text);
  if (!want) return false;
  return [...document.querySelectorAll('[data-rht-toaster] [role="status"]')]
    .some((n) => norm(n.textContent).includes(want));
}

// Friendly Vietnamese error formatter.
// - Ưu tiên Zod field issues (details.issues) → "<Field>: <reason>" cụ thể.
// - Fallback theo code (bad_input/unauthorized/forbidden/not_found...) sang text VN.
// Tránh để user thấy raw "bad_input: bad input".
function formatErrorForUser({ code, message, details }) {
  const issues = Array.isArray(details?.issues) ? details.issues : null;
  if (issues && issues.length > 0) {
    // Nhãn + câu đều qua Lingui (12/10/2026: khách EN gõ sai một ô là đọc «… là bắt buộc»).
    // `i18n` là instance của @cnv/ui/i18n — đã activate trong initI18n trước mọi request.
    const FIELD_LABEL = {
      search: msg`Từ khoá tìm kiếm`, email: 'Email', password: msg`Mật khẩu`, name: msg`Họ tên`,
      phone: msg`Số điện thoại`, employee_code: msg`Mã nhân viên`, department: msg`Phòng ban`,
      team_id: 'Team', role: msg`Vai trò`, uid: 'UID', limit: msg`Số lượng`, page: msg`Trang`,
    };
    const issueText = (it) => {
      const field = it?.path || it?.in || '';
      const raw = FIELD_LABEL[field];
      const label = raw ? (typeof raw === 'string' ? raw : i18n._(raw)) : (field || i18n._(msg`Trường`));
      const ic = it?.code;
      const detail = it?.message || '';
      if (ic === 'too_big') {
        const m = detail.match(/at most (\d+)/i);
        const max = m ? m[1] : 200;
        return i18n._(msg`${label} không được vượt quá ${max} ký tự`);
      }
      if (ic === 'too_small') {
        const m = detail.match(/at least (\d+)/i);
        const min = m ? m[1] : 1;
        return i18n._(msg`${label} phải có ít nhất ${min} ký tự`);
      }
      if (ic === 'invalid_string' && /email/i.test(detail)) return i18n._(msg`${label} không đúng định dạng email`);
      if (ic === 'invalid_type') return i18n._(msg`${label} sai kiểu dữ liệu`);
      if (ic === 'invalid_enum_value') return i18n._(msg`${label} không nằm trong các giá trị cho phép`);
      if (ic === 'required') return i18n._(msg`${label} là bắt buộc`);
      return detail ? `${label}: ${detail}` : i18n._(msg`${label} không hợp lệ`);
    };
    return issues.slice(0, 3).map(issueText).join(' · ');
  }
  // Lỗi PHÂN QUYỀN — phải nói rõ "thiếu quyền", đừng để user đoán là hệ thống lỗi.
  // Câu chuẩn do BE dựng kèm nhãn ô trên bảng Phân quyền (api/src/shared/permissionLabels.js) và
  // caller ở trên đã ưu tiên nó. Nhánh này chỉ chạy khi KHÔNG có câu đó: BE bản cũ chưa deploy,
  // hoặc gate bằng scope API-key (`scope_required`) còn trả chuỗi kỹ thuật 'requires scope ...'.
  // Cố ý KHÔNG in mã quyền ra cho end user — mã đã có trong `details` cho log/CSKH.
  if (code === 'permission_denied' || code === 'scope_required') {
    const isTechnical = !message || /^requires (permission|scope)\b/i.test(message);
    if (!isTechnical) return message;
    return i18n._(msg`Chưa được cấp quyền cho thao tác này — không phải lỗi hệ thống. Nhờ quản trị viên cấp thêm ở Cài đặt → Phân quyền.`);
  }
  const CODE_LABEL = {
    bad_input: msg`Dữ liệu nhập vào không hợp lệ`,
    unauthorized: msg`Phiên đăng nhập đã hết — vui lòng đăng nhập lại`,
    forbidden: msg`Bạn không có quyền thực hiện thao tác này`,
    not_found: msg`Không tìm thấy bản ghi`,
    conflict: msg`Dữ liệu trùng / đã tồn tại`,
    rate_limited: msg`Thao tác quá nhanh — thử lại sau ít giây`,
    rate_limit_exceeded: msg`Thao tác quá nhanh — thử lại sau ít giây`,
    server_error: msg`Hệ thống đang gặp lỗi — vui lòng thử lại sau`,
  };
  const friendly = CODE_LABEL[code];
  if (friendly) return i18n._(friendly);
  return message || code || i18n._(msg`Đã xảy ra lỗi không xác định`);
}

export const authApi = {
  async login(email, password) {
    const { data } = await inboxClient.post('/auth/login', { email, password });
    return data;
  },
  async loginWithGoogle(arg) {
    // arg: string (idToken — legacy GSI) HOẶC object { code } (auth-code flow lấy
    // luôn calendar scope) / { idToken }. BE googleLogin nhận cả idToken lẫn code.
    // User KHÔNG tồn tại → BE trả 403 account_not_registered (no JIT — security).
    const body = typeof arg === 'string' ? { idToken: arg } : (arg || {});
    const { data } = await inboxClient.post('/auth/google', body);
    return data;
  },
  async listMyTenants() {
    const { data } = await inboxClient.get('/auth/me/tenants');
    return data.data;
  },
  // Gửi kèm refreshToken hiện tại: refresh claim mang active_tenant_id nên đổi workspace mà
  // KHÔNG rotate refresh token thì ~15 phút sau lần refresh kế tiếp sẽ kéo user về workspace cũ.
  // BE trả refreshToken mới (có thể vắng nếu token legacy) — caller phải lưu lại.
  async switchTenant(tenant_id) {
    const refreshToken = useAuthStore.getState().refreshToken || undefined;
    const { data } = await inboxClient.post('/auth/me/switch-tenant', { tenant_id, refreshToken });
    return data;
  },
  // Tự mở thêm công ty + workspace (23/09/2026). Trần do máy chủ giữ — `allowance` chỉ để nút
  // nói đúng tình trạng TRƯỚC khi bấm; lượt tạo vẫn có thể 409 `workspace_limit_reached`.
  async workspaceAllowance() {
    const { data } = await inboxClient.get('/auth/me/workspaces/allowance');
    return data.data;
  },
  async createWorkspace(org_name) {
    // timezone: múi giờ máy người tạo → múi giờ workspace mới (cùng luật với đăng ký).
    const { data } = await inboxClient.post('/auth/me/workspaces', { org_name, timezone: deviceTimezone() });
    return data.data;
  },
};

export const inboxApi = {
  async listConversations(params = {}) {
    const { data } = await inboxClient.get('/inbox/conversations', { params });
    return data;
  },
  async getConversation(id) {
    const { data } = await inboxClient.get(`/inbox/conversations/${id}`);
    return data.data;
  },
  async createConversation(input) {
    const { data } = await inboxClient.post('/inbox/conversations', input);
    return data.data;
  },
  async updateStatus(id, status) {
    const { data } = await inboxClient.patch(`/inbox/conversations/${id}/status`, { status });
    return data.data;
  },
  async listMessages(conv_id, params = {}) {
    const { data } = await inboxClient.get(`/inbox/conversations/${conv_id}/messages`, { params });
    return data.data;
  },
  async sendMessage(conv_id, body) {
    const { data } = await inboxClient.post(`/inbox/conversations/${conv_id}/messages`, body);
    return data.data;
  },
};

// channelsApi đã thay bằng cnv-ui ChatChannelsPage gọi trực tiếp api/axios.

export const apiKeysApi = {
  async list() {
    const { data } = await inboxClient.get('/api-keys');
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/api-keys', body);
    return data.data;          // có thể có raw_key + warning
  },
  async revoke(id, reason) {
    const { data } = await inboxClient.post(`/api-keys/${id}/revoke`, { reason });
    return data.data;
  },
};

export const webhooksApi = {
  async list() {
    const { data } = await inboxClient.get('/webhooks');
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/webhooks', body);
    return data.data;          // có thể có secret + warning
  },
  async update(id, body) {
    const { data } = await inboxClient.patch(`/webhooks/${id}`, body);
    return data.data;
  },
  async rotateSecret(id) {
    const { data } = await inboxClient.post(`/webhooks/${id}/rotate-secret`, {});
    return data.data;
  },
  async remove(id) {
    await inboxClient.delete(`/webhooks/${id}`);
  },
  async listDeliveries(id, params = {}) {
    const { data } = await inboxClient.get(`/webhooks/${id}/deliveries`, { params });
    return data.data;
  },
  async replayDelivery(delivery_id) {
    const { data } = await inboxClient.post(`/webhook-deliveries/${delivery_id}/replay`, {});
    return data.data;
  },
};

export const invitationApi = {
  // Admin (JWT)
  async create(body) {
    const { data } = await inboxClient.post('/users/invitations', body);
    return data.data;
  },
  async list(status) {
    const { data } = await inboxClient.get('/users/invitations', {
      params: status ? { status } : {},
    });
    return data.data;
  },
  async revoke(id, reason) {
    await inboxClient.delete(`/users/invitations/${id}`, {
      data: { reason: reason || null },
    });
  },
  // Public (no JWT) — chỉ AcceptInvitePage gọi, và trang đó TỰ nói lỗi (màn lỗi / toast đã dịch).
  // `skipErrorToast`: không thì interceptor bắn thêm toast thứ hai bằng câu dự phòng tiếng Việt
  // của formatErrorForUser — người được mời ở Canada thấy một câu Anh + một câu Việt.
  async getInfo(token) {
    const { data } = await inboxClient.get(`/public/invitations/${encodeURIComponent(token)}`, { skipErrorToast: true });
    return data.data;
  },
  async redeem(token, body) {
    const { data } = await inboxClient.post(`/public/invitations/${encodeURIComponent(token)}/redeem`, body, { skipErrorToast: true });
    return data.data;
  },
  async accept(token, body) {
    const { data } = await inboxClient.post(`/public/invitations/${encodeURIComponent(token)}/accept`, body, { skipErrorToast: true });
    return data.data;
  },
};

// Gói dịch vụ + seat quota của tenant hiện tại (gói Free = 10 user). Dùng cho
// banner seat + disable nút mời + trang Billing (lịch sử license batch).
export const planApi = {
  async seatStatus() {
    const { data } = await inboxClient.get('/users/seat-status');
    return data.data;
  },
};

// N2/N4 — chính sách chấm công phân tầng + tính lại bảng công.
// Mount BE: /api/v1/attendance-policies/* (xem api/src/modules/attendance/routes/policiesV1.js)
export const attendancePolicyApi = {
  async list() {
    const { data } = await inboxClient.get('/attendance-policies');
    return data.data;
  },
  async scopes() {
    const { data } = await inboxClient.get('/attendance-policies/scopes');
    return data.data;
  },
  async exceptions() {
    const { data } = await inboxClient.get('/attendance-policies/exceptions');
    return data.data;
  },
  // Xem trước: một nhân viên vào một ngày sẽ thấy gì khi bấm chấm công.
  async preview(body) {
    const { data } = await inboxClient.post('/attendance-policies/preview', body);
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/attendance-policies', body);
    return data.data;
  },
  async update(id, body) {
    const { data } = await inboxClient.put(`/attendance-policies/${id}`, body);
    return data.data;
  },
  async disable(id) {
    const { data } = await inboxClient.delete(`/attendance-policies/${id}`);
    return data.data;
  },
  // Mặc định là chế độ THỬ. Phải truyền apply:true mới ghi thật.
  async recompute(body) {
    const { data } = await inboxClient.post('/attendance-policies/recompute', body);
    return data.data;
  },
};

// `attendanceFormApi` GỠ 20/08/2026 (ADR-037) cùng với `/api/v1/attendance-forms/*`. Phiếu
// hiện trường nay thuộc cụm VIỆC — helper tương ứng là `taskCheckinApi` (phiếu của dự án).

// M7 — lịch làm việc theo ngày + ca trống.
export const rosterApi = {
  // Định nghĩa ca (bảng `shifts`) — dùng chung endpoint của cụm Nhân sự, không đẻ bản sao.
  async shifts() {
    const { data } = await inboxClient.get('/shifts', { params: { limit: 200 } });
    return data.data;
  },
  async staff(params) {
    const { data } = await inboxClient.get('/attendance-roster/staff', { params });
    return data.data;
  },
  async grid(params) {
    const { data } = await inboxClient.get('/attendance-roster', { params });
    return data.data;
  },
  async mine(params) {
    const { data } = await inboxClient.get('/attendance-roster/mine', { params });
    return data.data;
  },
  async coverage(params) {
    const { data } = await inboxClient.get('/attendance-roster/coverage', { params });
    return data.data;
  },
  async assign(body) {
    const { data } = await inboxClient.post('/attendance-roster', body);
    // Kèm CẢNH BÁO (vd ca này chồng giờ ca khác cùng ngày). Máy chủ vẫn ghi — chồng giờ là
    // chuyện có thật ở 28% ô người-ngày của khách thật — nhưng người vừa bấm phải thấy ngay,
    // chứ đợi ai đó mở màn soát thì đã muộn.
    return { ...data.data, warnings: data.warnings || [] };
  },
  // `dry_run: true` trả về ĐÚNG kế hoạch mà lần ghi thật sẽ dùng (cùng một hàm ở BE).
  async bulk(body) {
    const { data } = await inboxClient.post('/attendance-roster/bulk', body);
    return data.data;
  },
  async copy(body) {
    const { data } = await inboxClient.post('/attendance-roster/copy', body);
    return data.data;
  },
  async remove(id) {
    const { data } = await inboxClient.delete(`/attendance-roster/${id}`);
    return data.data;
  },
  async openShifts(params) {
    const { data } = await inboxClient.get('/attendance-roster/open-shifts', { params });
    return data.data;
  },
  async createOpenShift(body) {
    const { data } = await inboxClient.post('/attendance-roster/open-shifts', body);
    return data.data;
  },
  async claimOpenShift(id) {
    const { data } = await inboxClient.post(`/attendance-roster/open-shifts/${id}/claim`);
    return data.data;
  },
  async cancelOpenShift(id) {
    const { data } = await inboxClient.delete(`/attendance-roster/open-shifts/${id}`);
    return data.data;
  },
};

// Kho ứng dụng — nguồn là LICENSE THẬT (`/marketplace/apps`), KHÔNG phải catalog seed cũ.
// Đường tự kích hoạt module (`POST /marketplace/modules/:code/activate`) đã GỠ ở BE 15/08:
// nó cấp quyền vĩnh viễn không hoá đơn. Nâng cấp từ nay đi qua yêu cầu → CNV kích hoạt.
export const marketplaceApi = {
  async apps() {
    const { data } = await inboxClient.get('/marketplace/apps');
    return data.data;
  },
  async assignees(key) {
    const { data } = await inboxClient.get(`/marketplace/apps/${key}/assignees`);
    return data.data;
  },
  async upgradeRequests() {
    const { data } = await inboxClient.get('/marketplace/upgrade-requests');
    return data.data;
  },
  async requestUpgrade(body) {
    const { data } = await inboxClient.post('/marketplace/upgrade-requests', body);
    return data.data;
  },
  async cancelUpgradeRequest(id) {
    const { data } = await inboxClient.delete(`/marketplace/upgrade-requests/${id}`);
    return data.data;
  },
};

// Ví credit + thanh toán gói (01/10/2026). Số dư đọc từ ví CDP; nạp + subscription qua Stripe
// Checkout (máy chủ trả `checkout_url`, trình duyệt chuyển sang đó). Giá do máy chủ chốt — ở đây
// chỉ gửi gói/số chỗ/kỳ. Xem docs/THANH_TOAN_STRIPE_VA_VI_CREDIT.md.
export const billingApi = {
  async overview() {
    const { data } = await inboxClient.get('/billing/overview');
    return data.data;
  },
  // Số dư cho thanh trên cùng — máy chủ đệm 60 giây; 403 ⇒ người này không xem được ví công ty.
  async balance() {
    // Gọi từ thanh trên cùng ở MỌI trang ⇒ lỗi không được bắn toast (interceptor toast mọi GET 4xx).
    const { data } = await inboxClient.get('/billing/credit/balance', { skipErrorToast: true });
    return data.data;
  },
  async creditHistory(params = {}) {
    const { data } = await inboxClient.get('/billing/credit/history', { params });
    return data.data;
  },
  async createTopup(amount) {
    const { data } = await inboxClient.post('/billing/credit/topups', { amount });
    return data.data;
  },
  async topup(id, { refresh = false } = {}) {
    const { data } = await inboxClient.get(`/billing/credit/topups/${id}`, { params: refresh ? { refresh: 1 } : {} });
    return data.data;
  },
  async quote({ plan_code, seats, period }) {
    const { data } = await inboxClient.get('/billing/plan-purchases/quote', { params: { plan_code, seats, period } });
    return data.data;
  },
  async purchase(body) {
    const { data } = await inboxClient.post('/billing/plan-purchases', body);
    return data.data;
  },
  async purchaseStatus(id, { refresh = false } = {}) {
    const { data } = await inboxClient.get(`/billing/plan-purchases/${id}`, { params: refresh ? { refresh: 1 } : {} });
    return data.data;
  },
  async portal() {
    const { data } = await inboxClient.post('/billing/portal', {});
    return data.data;
  },
};

// Tích hợp Connecteam — đồng bộ MỘT CHIỀU connecteam.com → CNV Connect.
// Không có hàm nào ghi ngược sang Connecteam, và sẽ không bao giờ có: mọi POST/PUT dưới đây
// ghi vào CSDL của mình (khoá, ánh xạ, lệnh chạy). Xem `api/src/modules/connecteam/`.
export const connecteamApi = {
  async connection() {
    const { data } = await inboxClient.get('/connecteam/connection');
    return data.data;
  },
  async saveConnection(body) {
    const { data } = await inboxClient.put('/connecteam/connection', body);
    return data.data;
  },
  async testConnection() {
    const { data } = await inboxClient.post('/connecteam/connection/test', {});
    return data.data;
  },
  // Webhook (13/10/2026) — Connecteam gửi tới ngay khi có thay đổi. Khoá bí mật chỉ trả MỘT lần lúc cấp.
  async webhook() {
    const { data } = await inboxClient.get('/connecteam/webhook');
    return data.data;
  },
  async newWebhookSecret() {
    const { data } = await inboxClient.post('/connecteam/webhook/secret', {});
    return data.data;
  },
  async removeWebhookSecret() {
    const { data } = await inboxClient.delete('/connecteam/webhook/secret');
    return data.data;
  },
  async entities() {
    const { data } = await inboxClient.get('/connecteam/entities');
    return data.data;
  },
  // Trả 202 ngay rồi chạy nền — một lượt đủ thực thể mất ~1 phút, giữ kết nối HTTP suốt
  // thời gian đó là ăn timeout. Tiến độ đọc lại bằng entities() + runs().
  async sync(body) {
    const { data } = await inboxClient.post('/connecteam/sync', body || {});
    return data.data;
  },
  async runs(params) {
    const { data } = await inboxClient.get('/connecteam/runs', { params });
    return data;
  },
  async mirror(entity, params) {
    const { data } = await inboxClient.get(`/connecteam/mirror/${entity}`, { params });
    return data;
  },
  async links(kind) {
    const { data } = await inboxClient.get('/connecteam/links', { params: { kind } });
    return data;
  },
  async saveLink(body) {
    const { data } = await inboxClient.post('/connecteam/links', body);
    return data.data;
  },
  async deleteLink(id) {
    const { data } = await inboxClient.delete(`/connecteam/links/${id}`);
    return data.data;
  },
  // Ba hàm dưới phục vụ thẻ "Đưa vào sử dụng". Đường dẫn ghép từ tham số thay vì viết
  // cứng 12 hàm: mọi nhịp chiếu đều cùng một hình dạng (GET .../preview → POST ...), nên
  // 12 hàm gần giống nhau chỉ là 12 chỗ để gõ sai.
  async applyPreview(nhip, params) {
    const { data } = await inboxClient.get(`/connecteam/apply/${nhip}/preview`, { params });
    return data.data;
  },
  async applyRun(nhip, body) {
    const { data } = await inboxClient.post(`/connecteam/apply/${nhip}`, body || {});
    return data.data;
  },
  async reconcile(params) {
    const { data } = await inboxClient.get('/connecteam/reconcile', { params });
    return data.data;
  },
  async autoMatch() {
    const { data } = await inboxClient.post('/connecteam/links/auto-match', {});
    return data.data;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Ảnh công trường: `jobsiteApi` đã CHUYỂN LÊN KERNEL — `@cnv/ui/lib/jobsiteApi`.
//
// Chuyển ngày 21/08/2026 khi cụm ảnh đổi điểm vào về việc + dự án, và Core/Work cũng sẽ
// nhận đủ cụm này. Giữ một bản chép ở đây là để hai bản trôi khỏi nhau: bản kernel thêm
// tham số mới, bản admin thì không, và trang nào import nhầm bản cũ sẽ hỏng lặng lẽ.
// ─────────────────────────────────────────────────────────────────────────────

// ── TỔNG QUAN CÔNG TRÌNH (27/08/2026) ──────────────────────────────────────────────────────
// Màn hình danh sách toàn bộ công trình + giai đoạn + bộ lọc đã lưu. BE ở
// `api/src/modules/tasks/routes/projectsOverviewV1.js`.
export const projectsApi = {
  async overview(params) {
    // Trả nguyên gói { data, groups, totals, page, limit } — KHÔNG bóc lấy mỗi `data` như các
    // hàm trên: cộng dồn theo nhóm và dòng tổng là phần chính của màn này, tính ở BE trên
    // TOÀN BỘ tập đã lọc chứ không phải trang đang xem.
    const { data } = await inboxClient.get('/tasks/projects/overview', { params });
    return data;
  },
  async update(id, body) {
    const { data } = await inboxClient.put(`/tasks/projects/${id}`, body);
    return data.data;
  },
  // Hồ sơ MỘT dự án (/projects/:id). `profile` = info + vận hành + giờ (quyền tasks:view);
  // `pnlOne` = tiền + tiền theo người (quyền reports:view) — hai đường hai quyền, xem ghi chú
  // ở BE projectsOverviewV1.js.
  async profile(id) {
    const { data } = await inboxClient.get(`/tasks/projects/${id}/profile`);
    return data.data;
  },
  // Thành viên dự án + hiệu suất (số task đang gánh · trễ · đã xong · đúng hạn · giờ công).
  async members(id) {
    const { data } = await inboxClient.get(`/tasks/projects/${id}/member-performance`);
    return data.data;
  },
  async pnlOne(id) {
    const { data } = await inboxClient.get(`/tasks/projects/${id}/pnl`);
    return data.data;
  },
  async stages() {
    const { data } = await inboxClient.get('/tasks/project-stages');
    return data.data;
  },
  async createStage(body) {
    const { data } = await inboxClient.post('/tasks/project-stages', body);
    return data.data;
  },
  async updateStage(id, body) {
    const { data } = await inboxClient.put(`/tasks/project-stages/${id}`, body);
    return data.data;
  },
  async deleteStage(id) {
    const { data } = await inboxClient.delete(`/tasks/project-stages/${id}`);
    return data.data;
  },
  // Bộ lọc đã lưu dùng CHUNG đường `/tasks/saved-views` + bảng `task_saved_views` có sẵn của
  // cụm Việc — chỉ khác `module`. Đừng mở đường thứ hai cho cùng một khái niệm.
  async savedViews(module = 'projects') {
    const { data } = await inboxClient.get('/tasks/saved-views', { params: { module } });
    return data.data;
  },
  async saveView({ name, params, is_shared }) {
    const { data } = await inboxClient.post('/tasks/saved-views', {
      module: 'projects', name, view_type: 'list', filters: params || {}, is_public: !!is_shared,
    });
    return data.data;
  },
  async deleteView(id) {
    const { data } = await inboxClient.delete(`/tasks/saved-views/${id}`);
    return data.data;
  },
};

// ── BÀN XẾP VIỆC (27/08/2026) ──────────────────────────────────────────────────────────────
// BE: `api/src/modules/tasks/routes/workScheduleV1.js`.
export const scheduleApi = {
  async grid(params) {
    // Nguyên gói { data, days, totals } — con số đầu mỗi cột ngày do BE tính trên toàn khoảng.
    const { data } = await inboxClient.get('/work-schedule', { params });
    return data;
  },
  async unscheduled(params) {
    const { data } = await inboxClient.get('/work-schedule/unscheduled', { params });
    return data.data;
  },
  async create(body) {
    const { data } = await inboxClient.post('/work-schedule', body);
    return data.data;
  },
  async update(id, body) {
    const { data } = await inboxClient.put(`/work-schedule/${id}`, body);
    return data.data;
  },
  async remove(id) {
    const { data } = await inboxClient.delete(`/work-schedule/${id}`);
    return data.data;
  },
  async check(body) {
    const { data } = await inboxClient.post('/work-schedule/check', body);
    return data.data;
  },
  async publish(body) {
    const { data } = await inboxClient.post('/work-schedule/publish', body);
    return data.data;
  },
  async copyWeek(body) {
    const { data } = await inboxClient.post('/work-schedule/copy-week', body);
    return data.data;
  },
  async suggest(params) {
    const { data } = await inboxClient.get('/work-schedule/suggest', { params });
    return data.data;
  },
};
